/**
 * Map /api/storage/* failure responses to actionable user copy.
 * Keeps Firebase session errors separate from local GCS ADC setup.
 */

export type StorageFailureKind =
  | 'firebase-auth-required'
  | 'firebase-auth-invalid'
  | 'gcs-auth-missing'
  | 'not-found'
  | 'other';

export interface ClassifiedStorageFailure {
  kind: StorageFailureKind;
  userMessage: string;
  /** When true, authedFetch may surface a debounced toast. */
  shouldNotify: boolean;
}

const GCS_ADC_SETUP = 'gcloud auth application-default login';

function bodyText(body: unknown): string {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') {
    const rec = body as Record<string, unknown>;
    const parts = [rec.error, rec.detail, rec.code, rec.setupCommand]
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
    return parts.join(' ');
  }
  return '';
}

export function classifyStorageFailure(input: {
  status: number;
  body?: unknown;
}): ClassifiedStorageFailure {
  const text = bodyText(input.body);
  const lower = text.toLowerCase();
  const hasGcsAuthCode = (input.body && typeof input.body === 'object'
    && (input.body as Record<string, unknown>).code === 'gcs_auth_missing');

  if (input.status === 401 && lower.includes('authentication required')) {
    return {
      kind: 'firebase-auth-required',
      userMessage: 'Sign in required to load saved reports and evidence. Refresh after signing in.',
      shouldNotify: true,
    };
  }

  if (input.status === 403 && lower.includes('invalid or expired token')) {
    return {
      kind: 'firebase-auth-invalid',
      userMessage: 'Your session expired. Sign out and sign back in, then retry.',
      shouldNotify: true,
    };
  }

  if (
    hasGcsAuthCode
    || (input.status === 503 && lower.includes('gcs'))
    || (input.status === 401 && /gcs_auth_missing|no metadata token|application default credentials/i.test(text))
  ) {
    const setup = (input.body && typeof input.body === 'object' && typeof (input.body as Record<string, unknown>).setupCommand === 'string')
      ? String((input.body as Record<string, unknown>).setupCommand)
      : GCS_ADC_SETUP;
    return {
      kind: 'gcs-auth-missing',
      userMessage: `Cloud storage is unavailable in local dev. Run \`${setup}\`, restart \`npm run dev\`, then retry.`,
      shouldNotify: true,
    };
  }

  if (input.status === 404) {
    return {
      kind: 'not-found',
      userMessage: 'Saved file not found in cloud storage.',
      shouldNotify: false,
    };
  }

  return {
    kind: 'other',
    userMessage: text ? `Storage request failed (${input.status}): ${text.slice(0, 160)}` : `Storage request failed (${input.status}).`,
    shouldNotify: false,
  };
}
